<sb-dialog-activation
  [currentStep]="currentStep"
  [totalSteps]="steps - 1"
  [showCloseButton]="currentStep === targetSettingDialogStep.TEASER"
  [showProgressBar]="currentStep !== targetSettingDialogStep.TEASER && currentStep !== targetSettingDialogStep.SUMMARY"
  [showBackButton]="currentStep !== targetSettingDialogStep.TEASER && currentStep !== targetSettingDialogStep.SUMMARY"
  (back)="goBack()"
  [hideSidePanel]="currentStep === targetSettingDialogStep.GUIDE"
>
  <div main-panel>
    @switch (currentStep) {
      @case (targetSettingDialogStep.TEASER) {
        <chapter-step
          [subtitle]="'Targets' | translate"
          [title]="'Save up to 15% on labour costs – set your targets now' | translate"
          [nextButtonLabel]="'Show me how' | translate"
          (next)="goToStep(targetSettingDialogStep.GUIDE)"
          [badge]="'BETA'"
        >
          <div class="flex flex-col gap-1">
            <p class="m-0 text-base">
              {{
                'Most hospitality businesses overspend on labour by 10–15% due to overstaffing, unbalanced wages, and inefficient scheduling. Setting smart labour cost targets helps you:'
                  | translate
              }}
            </p>
            <chapter-item
              [text]="'Match staffing to demand' | translate"
              [description]="'Reduce waste, avoid last-minute overtime.' | translate"
            />
            <chapter-item
              [text]="'Control wage costs' | translate"
              [description]="'Use higher-cost staff when they add the most value.' | translate"
            />
            <chapter-item
              [text]="'Keep labour costs in check' | translate"
              [description]="'Ensure spending stays balanced with revenue.' | translate"
            />
          </div>
        </chapter-step>
        <p class="mt-16 text-support">
          {{
            'Performance is in Early Access: currently free to use on Shiftbase Premium & Enterprise, but will eventually become a paid add-on.'
              | translate
          }}
        </p>
      }

      @case (targetSettingDialogStep.GUIDE) {
        <chapter-step
          [title]="
            'Here\'s exactly how Targets will help you make smarter scheduling decisions and reduce labour costs'
              | translate
          "
          [nextButtonLabel]="'Let\'s set it up' | translate"
          (next)="goToStep(targetSettingDialogStep.SETUP_PRODUCTIVITY_TARGET)"
        >
          <div class="relative h-0 rounded-md pb-iframe-video">
            <iframe
              [src]="loomVideoLink"
              frameborder="0"
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen
              class="absolute top-0 left-0 size-full"
            ></iframe>
          </div>
        </chapter-step>
      }
      @case (targetSettingDialogStep.SETUP_PRODUCTIVITY_TARGET) {
        <chapter-step
          [title]="'Let\'s set your productivity target' | translate"
          (next)="goToStep(targetSettingDialogStep.SETUP_HOURLY_WAGE)"
          [nextButtonDisabled]="formGroup.controls.productivity.invalid"
        >
          <target-setting-setup
            [description]="
              'Productivity shows how much revenue you generate per labour hour. By tracking it, you can fine-tune staffing to meet demand without overspending.'
                | translate
            "
            [explanation]="
              'Most businesses like yours aim for €55-€65 per hour. If you\'re bringing in €6,000 on a day with 100 hours worked, you\'re at €60/hour.'
                | translate
            "
            [fieldLabel]="'Productivity target for [currentYear]' | translate: { currentYear }"
            [placeholder]="'Usually between €55 and €65' | translate"
            [control]="formGroup.controls.productivity"
            [prefix]="'currency'"
          ></target-setting-setup>
        </chapter-step>
      }
      @case (targetSettingDialogStep.SETUP_HOURLY_WAGE) {
        <chapter-step
          [title]="'Next, set your average hourly wage target' | translate"
          (next)="goToStep(targetSettingDialogStep.SETUP_LABOURCOST_PERCENTAGE)"
          [nextButtonDisabled]="formGroup.controls.averageHourlyWage.invalid"
        >
          <target-setting-setup
            [description]="
              'This is the average pay per hour worked. Setting a target helps keep wages balanced, ensuring fair pay while controlling costs.'
                | translate
            "
            [explanation]="
              'Look at what you\'re actually paying now. Most businesses in your industry land around €19-€22. Don\'t worry—you can always adjust it later as your team changes.'
                | translate
            "
            [fieldLabel]="'Avg. hourly wage target for [currentYear]' | translate: { currentYear }"
            [placeholder]="'Usually between €19 and €22' | translate"
            [control]="formGroup.controls.averageHourlyWage"
            [prefix]="'currency'"
          ></target-setting-setup>
        </chapter-step>
      }
      @case (targetSettingDialogStep.SETUP_LABOURCOST_PERCENTAGE) {
        <chapter-step
          [title]="'Finally, set your labour cost percentage target' | translate"
          (next)="goToStep(targetSettingDialogStep.TURNOVER_GUIDANCE)"
          [nextButtonDisabled]="formGroup.controls.laborCostPercentage.invalid || formGroup.invalid"
        >
          <target-setting-setup
            [description]="'This is the percentage of revenue spent on wages, training, and perks.' | translate"
            [explanation]="
              'Most successful businesses keep this between 29%-35%. Too low, and you might be understaffed. Too high, and your profits take a hit.'
                | translate
            "
            [fieldLabel]="'Labour cost % target for [currentYear]' | translate: { currentYear }"
            [placeholder]="'Usually between 29% and 35%' | translate"
            [control]="formGroup.controls.laborCostPercentage"
            [prefix]="'percentage'"
          ></target-setting-setup>

          @if (formGroup.invalid) {
            <p class="m-0 text-sm font-medium text-support">
              {{
                'You must add at least one of the three targets to continue. You can always adjust them later.'
                  | translate
              }}
            </p>
          }
        </chapter-step>
      }
      @case (targetSettingDialogStep.TURNOVER_GUIDANCE) {
        <chapter-step
          [title]="'Keep your turnover forecast up to date' | translate"
          (next)="goToStep(targetSettingDialogStep.WAGES)"
          [nextButtonLabel]="'Understood' | translate"
          (skip)="redirectToHelpCenter('adding-a-forecast')"
          [showSkipButton]="true"
          [skipButtonLabel]="'Help Center Article' | translate"
          [skipButtonIcon]="'external-link'"
        >
          <p class="m-0 text-base">
            {{
              "Your labour cost targets depend on accurate turnover forecasts. Make sure they're regularly updated so you can schedule with confidence. "
                | translate
            }}
          </p>
        </chapter-step>
      }
      @case (targetSettingDialogStep.WAGES) {
        @if (wagesSet) {
          <chapter-step
            [title]="'Wages are set—you\'re good to go.' | translate"
            (next)="savePreferencesAndGoToLastStep()"
            [nextButtonLoading]="nextButtonLoading"
            [nextButtonLabel]="'Got it' | translate"
          >
            <p class="m-0 text-base">
              {{
                'Over 90% of wages are configured, so your labour cost targets will be accurate. Just remember to keep wages updated for the best results.'
                  | translate
              }}
            </p>
          </chapter-step>
        } @else {
          <chapter-step
            [title]="'Set up wages for accurate targets' | translate"
            (next)="savePreferencesAndGoToLastStep()"
            [nextButtonLabel]="'Got it' | translate"
            [nextButtonLoading]="nextButtonLoading"
            [showSkipButton]="true"
            [skipButtonLabel]="'Help Center Article' | translate"
            [skipButtonIcon]="'external-link'"
            (skip)="redirectToHelpCenter('employee-contracts')"
          >
            <p class="m-0 text-base">
              {{
                "Less than 90% of wages are configured. Since wages are key to your labour cost targets, it's best to get them fully set up."
                  | translate
              }}
            </p>
          </chapter-step>
        }
      }
      @case (targetSettingDialogStep.SUMMARY) {
        <chapter-step
          [title]="'🎉 ' + ('Excellent! Targets are set—time to start optimising' | translate)"
          (next)="close()"
          [nextButtonLabel]="'Close and start optimising' | translate"
        >
          <chapter-item
            [text]="'Targets for each department' | translate"
            [description]="
              'Your labour cost targets are now live across all departments. You can adjust them or switch to monthly targets at any time in the Targets settings'
                | translate
            "
            [learnMoreLink]="'getting-started-with-insights-early-access' | knowledgeBaseArticleLink"
          />
          <chapter-item
            [text]="'What about planners?' | translate"
            [description]="'Planners will get a guide on how to use targets directly in the schedule.' | translate"
          />
        </chapter-step>
      }
    }
  </div>
  @if (currentStep !== targetSettingDialogStep.GUIDE) {
    <div
      class="flex size-full min-h-72 overflow-hidden rounded-lg bg-cover bg-center bg-no-repeat lg:mr-12"
      aside-panel
      [ngStyle]="{
        backgroundImage: 'url(assets/images/onboarding/chapters/target-setting.jpg)',
      }"
    ></div>
  }
</sb-dialog-activation>
